import type { FlowDefinition } from '../types/flow';

const API_BASE = '/api/flows';

export class FlowService {
  private getToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  private headers(): Record<string, string> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = this.getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }

  async listFlows(): Promise<FlowDefinition[]> {
    try {
      const res = await fetch(API_BASE, { headers: this.headers() });
      const data = await res.json();
      if (data.success) return data.flows;
      throw new Error(data.error || 'Failed to list flows');
    } catch (err: any) {
      console.error('[FlowService] listFlows error:', err.message);
      return [];
    }
  }

  async getFlow(id: string): Promise<FlowDefinition | null> {
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, { headers: this.headers() });
      const data = await res.json();
      if (data.success) return data.flow;
      throw new Error(data.error || 'Flow not found');
    } catch (err: any) {
      console.error('[FlowService] getFlow error:', err.message);
      return null;
    }
  }

  async createFlow(flow: FlowDefinition): Promise<FlowDefinition | null> {
    try {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: this.headers(),
        body: JSON.stringify(flow),
      });
      const data = await res.json();
      if (data.success) return data.flow;
      throw new Error(data.error || 'Failed to create flow');
    } catch (err: any) {
      console.error('[FlowService] createFlow error:', err.message);
      return null;
    }
  }

  async updateFlow(id: string, updates: Partial<FlowDefinition>): Promise<FlowDefinition | null> {
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: this.headers(),
        body: JSON.stringify(updates),
      });
      const data = await res.json();
      if (data.success) return data.flow;
      throw new Error(data.error || 'Failed to update flow');
    } catch (err: any) {
      console.error('[FlowService] updateFlow error:', err.message);
      return null;
    }
  }

  async deleteFlow(id: string): Promise<boolean> {
    try {
      const res = await fetch(`${API_BASE}/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: this.headers(),
      });
      const data = await res.json();
      return data.success && data.deleted;
    } catch (err: any) {
      console.error('[FlowService] deleteFlow error:', err.message);
      return false;
    }
  }
}

export const flowService = new FlowService();